import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plane, MapPin, Weight, Eye, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Spinner } from "../components/common";
import api, { apiError } from "../lib/api";
import { rupiah, fmtDate } from "../lib/format";
import { useI18n } from "../lib/i18n";

export default function PostTrip() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ categories: [], destinations: [] });
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    from_city: "", from_country: "Indonesia", to_city: "", destination_code: "",
    departure_date: "", return_date: "", capacity_kg: 10, service_fee: 50000,
    categories: [], notes: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  const toggleCat = (c) => setForm((f) => ({
    ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c],
  }));

  const chooseDest = (code) => {
    const d = meta.destinations.find((x) => x.code === code);
    setForm((f) => ({ ...f, destination_code: code, to_city: d?.name || "", to_country: d?.country || "" }));
  };

  const valid = form.from_city && form.to_city && form.departure_date && form.return_date;

  const publish = async () => {
    setBusy(true);
    try {
      await api.post("/trips", {
        ...form, capacity_kg: Number(form.capacity_kg), service_fee: Number(form.service_fee),
        to_country: meta.destinations.find((x) => x.code === form.destination_code)?.country || "",
      });
      toast.success("Trip dipublikasikan! 🎉");
      navigate("/my-trips");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="font-heading font-extrabold text-2xl text-navy flex items-center gap-2"><Plane className="text-sky" /> Create Your Trip</h1>
        <p className="text-muted-foreground text-sm mt-1">Beritahu kemana kamu pergi dan barang apa yang bisa kamu bawakan.</p>
      </div>

      {!preview ? (
        <div className="bg-white rounded-3xl p-6 shadow-soft border border-black/5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-sm font-semibold text-ink">From (city)</label>
              <Input data-testid="trip-from" value={form.from_city} onChange={set("from_city")} className="rounded-xl mt-1" placeholder="Medan" /></div>
            <div><label className="text-sm font-semibold text-ink">To (destination)</label>
              <select data-testid="trip-dest" value={form.destination_code} onChange={(e) => chooseDest(e.target.value)}
                className="w-full h-9 rounded-xl border border-input px-3 text-sm mt-1 bg-transparent">
                <option value="">Pilih destinasi</option>
                {meta.destinations.map((d) => <option key={d.code} value={d.code}>{d.flag} {d.name}</option>)}
              </select></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-sm font-semibold text-ink">Departure</label>
              <Input data-testid="trip-departure" type="date" value={form.departure_date} onChange={set("departure_date")} className="rounded-xl mt-1" /></div>
            <div><label className="text-sm font-semibold text-ink">Return</label>
              <Input data-testid="trip-return" type="date" value={form.return_date} onChange={set("return_date")} className="rounded-xl mt-1" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-sm font-semibold text-ink">Available capacity (kg)</label>
              <Input data-testid="trip-capacity" type="number" value={form.capacity_kg} onChange={set("capacity_kg")} className="rounded-xl mt-1" /></div>
            <div><label className="text-sm font-semibold text-ink">Service fee / item (Rp)</label>
              <Input data-testid="trip-fee" type="number" value={form.service_fee} onChange={set("service_fee")} className="rounded-xl mt-1" /></div>
          </div>
          <div>
            <label className="text-sm font-semibold text-ink">Accepted categories</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {meta.categories.map((c) => (
                <button key={c} type="button" onClick={() => toggleCat(c)} data-testid={`trip-cat-${c}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    form.categories.includes(c) ? "bg-navy text-white" : "bg-cloud text-navy"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div><label className="text-sm font-semibold text-ink">Notes (optional)</label>
            <Input data-testid="trip-notes" value={form.notes} onChange={set("notes")} className="rounded-xl mt-1" placeholder="Catatan tambahan" /></div>

          <button disabled={!valid} onClick={() => setPreview(true)} data-testid="trip-preview-btn"
            className="w-full rounded-full bg-navy text-white font-bold py-3 flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-navy-700 transition-colors">
            <Eye size={16} /> Preview
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-3xl p-6 shadow-soft border border-black/5">
            <span className="text-xs font-bold text-sky uppercase">Preview</span>
            <div className="flex items-center gap-2 font-heading font-bold text-navy text-lg mt-2">
              <MapPin size={18} className="text-sky" /> {form.from_city} → {form.to_city}
            </div>
            <div className="mt-3 space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Plane size={15} /> {fmtDate(form.departure_date)} → {fmtDate(form.return_date)}</div>
              <div className="flex items-center gap-2"><Weight size={15} /> {form.capacity_kg} kg available</div>
              <div className="font-semibold text-ink">{rupiah(form.service_fee)}/item</div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {form.categories.map((c) => <span key={c} className="text-xs bg-cloud rounded-full px-3 py-1 text-navy font-medium">{c}</span>)}
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setPreview(false)} data-testid="trip-back-btn" className="flex-1 rounded-full bg-white border border-black/10 text-navy font-bold py-3">Edit</button>
            <button onClick={publish} disabled={busy} data-testid="trip-publish-btn"
              className="flex-1 rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              <CheckCircle2 size={16} /> {t("publish_trip")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
